/**
 * ImportWizard - Main CSV import wizard component
 * 
 * Orchestrates the complete import flow:
 * 1. File upload (ImportDropzone)
 * 2. Preview/validation (ImportPreview)
 * 3. Progress tracking (ImportProgress)
 * 4. Result display
 */

import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationScope } from '../contexts/OrganizationScopeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Download,
  ArrowRight,
  RefreshCw,
  FileSpreadsheet,
  Upload,
  Eye,
  Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useImport, IMPORT_STEPS, IMPORT_STATUS, getImportRowErrorMessage } from '../hooks/useImport';
import { ImportDropzone } from './ImportDropzone';
import { ImportPreview } from './ImportPreview';
import { ImportProgress } from './ImportProgress';

// Max row errors listed on the result screen; the rest is in the error CSV
const RESULT_ERROR_LIMIT = 20;

// Step indicator data
const STEPS = [
  { key: IMPORT_STEPS.UPLOAD, icon: Upload, labelKey: 'step1' },
  { key: IMPORT_STEPS.PREVIEW, icon: Eye, labelKey: 'step2' },
  { key: IMPORT_STEPS.RESULT, icon: CheckCircle2, labelKey: 'step3' },
];

/**
 * ImportWizard component
 * 
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onOpenChange - Dialog open change handler
 * @param {string} props.entityType - 'pilots' | 'aircraft' | 'batteries' | 'documents'
 * @param {Function} props.onComplete - Callback when import completes
 */
export function ImportWizard({
  open,
  onOpenChange,
  entityType,
  onComplete,
}) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;

  // Use the import hook
  const {
    step,
    status,
    file,
    preview,
    job,
    result,
    error,
    settings,
    canConfirm,
    importableCount,
    reset,
    selectFile,
    uploadAndPreview,
    confirmImport,
    cancelImport,
    goBack,
    updateSettings,
    downloadErrors,
    downloadTemplate,
  } = useImport(entityType, scopedOrganizationId, onComplete);

  // Handle dialog close
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      // Reset state when closing
      reset();
    }
    onOpenChange(newOpen);
  };

  // Handle close button
  const handleClose = () => {
    handleOpenChange(false);
  };

  // Handle "Import Another" action
  const handleImportAnother = () => {
    reset();
  };

  // Handle "View in List" action
  const handleViewInList = () => {
    handleClose();
  };

  // Get entity type label
  const getEntityLabel = () => {
    return t.import?.entityTypes?.[entityType] || entityType;
  };

  // Get step index for progress indicator
  const getCurrentStepIndex = () => {
    if (step === IMPORT_STEPS.UPLOAD) return 0;
    if (step === IMPORT_STEPS.PREVIEW) return 1;
    if (step === IMPORT_STEPS.PROGRESS) return 1; // Still in step 2
    return 2;
  };

  // Get error message
  const getErrorMessage = (errorCode) => {
    if (!errorCode) return null;
    return t.import?.errors?.[errorCode] || t.import?.[errorCode] || errorCode;
  };

  // Determine result type. Row errors are failed rows and invalid rows skipped with their errors — the
  // backend's completed_with_errors (failed > 0 || errors > 0), which the hook shows as COMPLETED too.
  const getResultType = () => {
    if (status === IMPORT_STATUS.COMPLETED && result) {
      const hasFailedRows = result.failed > 0;
      if (!hasFailedRows && !(result.errors?.length > 0)) {
        return 'success';
      }
      // Errors, but nothing failed (invalid rows were only skipped) or some rows were written
      if (!hasFailedRows || result.imported > 0 || result.updated > 0) {
        return 'partial';
      }
    }
    if (status === IMPORT_STATUS.CANCELLED) {
      return 'cancelled';
    }
    return 'failed';
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="w-[95vw] max-w-[1440px] grid-cols-[minmax(0,1fr)] max-h-[90vh] overflow-y-auto"
        data-testid="import-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            {t.import?.title || 'Import from CSV'}
          </DialogTitle>
          <DialogDescription>
            {t.import?.subtitle || 'Bulk import data from CSV file'} — {getEntityLabel()}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 py-4">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isActive = getCurrentStepIndex() === idx;
            const isCompleted = getCurrentStepIndex() > idx;
            const isProgress = step === IMPORT_STEPS.PROGRESS && idx === 1;

            return (
              <div key={s.key} className="flex items-center">
                {idx > 0 && (
                  <div className={cn(
                    'w-12 h-0.5 mx-2',
                    isCompleted ? 'bg-primary' : 'bg-slate-200'
                  )} />
                )}
                <div className="flex flex-col items-center gap-1">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center transition-all',
                    isActive && 'bg-primary text-white',
                    isCompleted && 'bg-primary text-white',
                    !isActive && !isCompleted && 'bg-slate-100 text-slate-400'
                  )}>
                    {isProgress ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={cn(
                    'text-xs font-medium',
                    isActive ? 'text-primary' : 'text-slate-500'
                  )}>
                    {t.import?.[s.labelKey] || `Step ${idx + 1}`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="py-2">
          {/* Upload Step */}
          {step === IMPORT_STEPS.UPLOAD && (
            <ImportDropzone
              onFileSelect={selectFile}
              onUpload={uploadAndPreview}
              onDownloadTemplate={downloadTemplate}
              file={file}
              uploading={status === IMPORT_STATUS.UPLOADING}
              error={error}
              entityType={entityType}
            />
          )}

          {/* Preview Step */}
          {step === IMPORT_STEPS.PREVIEW && (
            <ImportPreview
              preview={preview}
              settings={settings}
              onUpdateSettings={updateSettings}
              onConfirm={confirmImport}
              onBack={goBack}
              confirming={status === IMPORT_STATUS.CONFIRMING}
              importableCount={importableCount}
              error={error}
            />
          )}

          {/* Progress Step */}
          {step === IMPORT_STEPS.PROGRESS && (
            <ImportProgress
              job={job}
              totalRows={preview?.total_rows || 0}
              onCancel={cancelImport}
            />
          )}

          {/* Result Step */}
          {step === IMPORT_STEPS.RESULT && (
            <ResultDisplay
              result={result}
              resultType={getResultType()}
              error={error}
              t={t}
              onDownloadErrors={downloadErrors}
              onImportAnother={handleImportAnother}
              onViewInList={handleViewInList}
              getErrorMessage={getErrorMessage}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Result display sub-component
 */
function ResultDisplay({
  result,
  resultType,
  error,
  t,
  onDownloadErrors,
  onImportAnother,
  onViewInList,
  getErrorMessage,
}) {
  const getResultConfig = () => {
    switch (resultType) {
      case 'success':
        return {
          icon: CheckCircle2,
          iconClass: 'text-emerald-500',
          bgClass: 'bg-emerald-50',
          borderClass: 'border-emerald-200',
          title: t.import?.resultSuccess || 'Import completed successfully',
        };
      case 'partial':
        return {
          icon: AlertTriangle,
          iconClass: 'text-amber-500',
          bgClass: 'bg-amber-50',
          borderClass: 'border-amber-200',
          title: t.import?.resultPartial || 'Import completed with errors',
        };
      case 'cancelled':
        return {
          icon: XCircle,
          iconClass: 'text-slate-500',
          bgClass: 'bg-slate-50',
          borderClass: 'border-slate-200',
          title: t.import?.resultCancelled || 'Import cancelled',
        };
      default:
        return {
          icon: XCircle,
          iconClass: 'text-red-500',
          bgClass: 'bg-red-50',
          borderClass: 'border-red-200',
          title: t.import?.resultFailed || 'Import failed',
        };
    }
  };

  const config = getResultConfig();
  const Icon = config.icon;
  const rowErrors = result?.errors || [];
  const listedErrors = rowErrors.slice(0, RESULT_ERROR_LIMIT);

  return (
    <div className="space-y-6">
      {/* Result Banner */}
      <Card className={cn(config.bgClass, config.borderClass)} data-testid="import-result-banner">
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className={cn('p-4 rounded-full', config.bgClass)}>
              <Icon className={cn('h-12 w-12', config.iconClass)} />
            </div>
            <h3 className="text-xl font-semibold text-slate-800">
              {config.title}
            </h3>
            {error && (
              <p className="text-sm text-red-600">
                {getErrorMessage(error)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Result Stats */}
      {result && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">{t.import?.imported || 'Imported'}</p>
              <p className="text-2xl font-bold text-emerald-600">{result.imported || 0}</p>
            </CardContent>
          </Card>
          
          {result.updated > 0 && (
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-500">{t.import?.updated || 'Updated'}</p>
                <p className="text-2xl font-bold text-blue-600">{result.updated}</p>
              </CardContent>
            </Card>
          )}
          
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">{t.import?.skipped || 'Skipped'}</p>
              <p className="text-2xl font-bold text-amber-600">{result.skipped || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">{t.import?.failed || 'Failed'}</p>
              <p className="text-2xl font-bold text-red-600">{result.failed || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Row Errors (invalid rows, skipped or failed, and rows the server could not write) */}
      {rowErrors.length > 0 && (
        <Card data-testid="import-errors-list">
          <CardContent className="p-0">
            <p className="flex items-center gap-2 px-4 pt-4 pb-2 text-sm font-medium">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              {t.import?.errorsTitle?.replace('{count}', rowErrors.length) || `Row errors (${rowErrors.length})`}
            </p>
            <div className="max-h-72 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[var(--bg-elevated)]">
                    <TableHead className="w-16 text-center">{t.import?.rowNumber || 'Row'}</TableHead>
                    <TableHead>{t.import?.errorsField || 'Field'}</TableHead>
                    <TableHead>{t.import?.errorsValue || 'Value'}</TableHead>
                    <TableHead>{t.import?.errorsMessage || 'Error'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listedErrors.map((rowError, idx) => (
                    <TableRow key={idx} data-testid={`import-error-row-${idx}`}>
                      <TableCell className="text-center text-sm text-[var(--text-secondary)]">
                        {rowError.row}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{rowError.field || '—'}</TableCell>
                      <TableCell className="max-w-[12rem] truncate font-mono text-xs" title={rowError.value || undefined}>
                        {rowError.value || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-red-600 dark:text-red-400" title={rowError.message || undefined}>
                        {getImportRowErrorMessage(t, rowError)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {rowErrors.length > listedErrors.length && (
              <p className="px-4 py-3 border-t text-xs text-[var(--text-secondary)]" data-testid="import-errors-more">
                {t.import?.errorsShownOf
                  ?.replace('{shown}', listedErrors.length)
                  .replace('{total}', rowErrors.length) ||
                  `Showing the first ${listedErrors.length} of ${rowErrors.length} errors.`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Download Errors */}
      {(rowErrors.length > 0 || result?.failed > 0) && (
        <Button
          variant="outline"
          onClick={onDownloadErrors}
          className="w-full gap-2"
          data-testid="download-errors-btn"
        >
          <Download className="h-4 w-4" />
          {t.import?.downloadErrors || 'Download Errors (CSV)'}
        </Button>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="outline"
          onClick={onImportAnother}
          className="gap-2"
          data-testid="import-another-btn"
        >
          <RefreshCw className="h-4 w-4" />
          {t.import?.importAnother || 'Import Another'}
        </Button>
        <Button
          onClick={onViewInList}
          className="gap-2"
          data-testid="view-in-list-btn"
        >
          {t.import?.viewInList || 'View in List'}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default ImportWizard;
